import { ArrowDownTrayIcon, CheckIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    ActionIcon,
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Group,
    Loader,
    Select,
    Stack,
    Table,
    Text,
    Textarea,
} from '@mantine/core';
import { useState } from 'react';
import { CandidateStatus, ShortlistEntry } from '../../../models/opportunity.model';
import { ExportService } from '../../../services/exportService';
import { useRemoveShortlistEntry, useUpsertShortlistEntry } from '../hooks/useShortlistMutations';
import { useOpportunityShortlistQuery } from '../hooks/useShortlistQuery';

const STATUS_OPTIONS = [
    { value: String(CandidateStatus.Shortlisted), label: 'Shortlisted' },
    { value: String(CandidateStatus.Contacted), label: 'Contacted' },
    { value: String(CandidateStatus.Rejected), label: 'Rejected' },
    { value: String(CandidateStatus.Proposed), label: 'Proposed' },
];

const getAvatarSrc = (photoUrl: string | null): string | undefined => {
    if (!photoUrl) return undefined;
    if (photoUrl.startsWith('data:') || photoUrl.startsWith('http')) return photoUrl;
    if (/^[A-Za-z0-9+/=]+$/.test(photoUrl)) return `data:image/jpeg;base64,${photoUrl}`;
    return undefined;
};

// ── Card row ──────────────────────────────────────────────────────────────────

interface CardProps {
    entry: ShortlistEntry;
    opportunityId: number;
    onOpenPreview: (personId: number) => void;
}

const ShortlistCard = ({ entry, opportunityId, onOpenPreview }: CardProps) => {
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesValue, setNotesValue] = useState(entry.notes ?? '');

    const upsertMutation = useUpsertShortlistEntry(opportunityId, entry.positionId);
    const removeMutation = useRemoveShortlistEntry(opportunityId);

    const handleStatusChange = (value: string | null) => {
        if (!value) { return; }
        upsertMutation.mutate({
            personId: entry.personId,
            status: Number(value) as CandidateStatus,
            notes: entry.notes ?? null,
        });
    };

    const handleSaveNotes = () => {
        upsertMutation.mutate(
            {
                personId: entry.personId,
                status: entry.candidateStatus,
                notes: notesValue.trim() || null,
            },
            { onSuccess: () => setEditingNotes(false) },
        );
    };

    const handleCancelEdit = () => {
        setNotesValue(entry.notes ?? '');
        setEditingNotes(false);
    };

    const handleRemove = () => {
        removeMutation.mutate({ positionId: entry.positionId, personId: entry.personId });
    };

    const addedByName =
        [entry.addedByManagerFirstName, entry.addedByManagerLastName]
            .filter(Boolean)
            .join(' ') || entry.addedByManagerEmail;

    return (
        <Box
            py="sm"
            style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
        >
            {/* Top row: avatar + name + actions */}
            <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Group
                    gap="sm"
                    wrap="nowrap"
                    style={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
                    onClick={() => !editingNotes && onOpenPreview(entry.personId)}
                >
                    <Avatar
                        src={getAvatarSrc(entry.photoUrl)}
                        size="md"
                        radius="xl"
                        alt={`${entry.firstName} ${entry.lastName}`}
                        style={{ flexShrink: 0 }}
                    >
                        {entry.firstName[0]}{entry.lastName[0]}
                    </Avatar>
                    <Box style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} style={{ color: 'var(--mantine-color-anchor)' }} truncate>
                            {entry.firstName} {entry.lastName}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>{entry.email}</Text>
                        {entry.fitScoreAtShortlist != null && (
                            <Badge size="xs" variant="light" color="sopraBlue" mt={2}>
                                Fit {entry.fitScoreAtShortlist}%
                            </Badge>
                        )}
                    </Box>
                </Group>
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={() => setEditingNotes(true)}
                        disabled={editingNotes}
                        aria-label="Edit notes"
                    >
                        <PencilIcon style={{ width: 14, height: 14 }} />
                    </ActionIcon>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        loading={removeMutation.isPending}
                        onClick={handleRemove}
                        aria-label="Remove from shortlist"
                    >
                        <TrashIcon style={{ width: 14, height: 14 }} />
                    </ActionIcon>
                </Group>
            </Group>

            {/* Status row */}
            <Group gap="sm" mt="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                <Select
                    data={STATUS_OPTIONS}
                    value={String(entry.candidateStatus)}
                    onChange={handleStatusChange}
                    size="xs"
                    w={140}
                    disabled={upsertMutation.isPending}
                    comboboxProps={{ withinPortal: true }}
                />
                <Text size="xs" c="dimmed">
                    Added by {addedByName} · {new Date(entry.addedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Text>
            </Group>

            {/* Notes */}
            {editingNotes && (
                <Stack gap={6} mt="xs" onClick={(e) => e.stopPropagation()}>
                    <Textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.currentTarget.value)}
                        size="xs"
                        autosize
                        minRows={2}
                        maxRows={8}
                        placeholder="Add notes about this candidate…"
                        autoFocus
                    />
                    <Group gap={6}>
                        <Button
                            size="xs"
                            variant="filled"
                            color="brand.5"
                            leftSection={<CheckIcon style={{ width: 12, height: 12 }} />}
                            onClick={handleSaveNotes}
                            loading={upsertMutation.isPending}
                        >
                            Save
                        </Button>
                        <ActionIcon
                            size="sm"
                            variant="subtle"
                            onClick={handleCancelEdit}
                            disabled={upsertMutation.isPending}
                            aria-label="Cancel"
                        >
                            <XMarkIcon style={{ width: 12, height: 12 }} />
                        </ActionIcon>
                    </Group>
                </Stack>
            )}
            {!editingNotes && entry.notes && (
                <Text size="xs" c="dimmed" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
                    {entry.notes}
                </Text>
            )}
        </Box>
    );
};

// ── Desktop table row ─────────────────────────────────────────────────────────

interface RowProps {
    entry: ShortlistEntry;
    opportunityId: number;
    onOpenPreview: (personId: number) => void;
}

const ShortlistRow = ({ entry, opportunityId, onOpenPreview }: RowProps) => {
    const [editingNotes, setEditingNotes] = useState(false);
    const [notesValue, setNotesValue] = useState(entry.notes ?? '');

    const upsertMutation = useUpsertShortlistEntry(opportunityId, entry.positionId);
    const removeMutation = useRemoveShortlistEntry(opportunityId);

    const handleStatusChange = (value: string | null) => {
        if (!value) { return; }
        upsertMutation.mutate({ personId: entry.personId, status: Number(value) as CandidateStatus, notes: entry.notes ?? null });
    };

    const handleSaveNotes = () => {
        upsertMutation.mutate(
            { personId: entry.personId, status: entry.candidateStatus, notes: notesValue.trim() || null },
            { onSuccess: () => setEditingNotes(false) },
        );
    };

    const handleCancelEdit = () => { setNotesValue(entry.notes ?? ''); setEditingNotes(false); };
    const handleRemove = () => { removeMutation.mutate({ positionId: entry.positionId, personId: entry.personId }); };

    const addedByName =
        [entry.addedByManagerFirstName, entry.addedByManagerLastName].filter(Boolean).join(' ') || entry.addedByManagerEmail;

    return (
        <>
            <Table.Tr onClick={() => !editingNotes && onOpenPreview(entry.personId)} style={{ cursor: editingNotes ? 'default' : 'pointer' }}>
                <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                        <Avatar src={getAvatarSrc(entry.photoUrl)} size="sm" radius="xl" alt={`${entry.firstName} ${entry.lastName}`}>
                            {entry.firstName[0]}{entry.lastName[0]}
                        </Avatar>
                        <Box>
                            <Text size="sm" fw={500}>{entry.firstName} {entry.lastName}</Text>
                            <Text size="xs" c="dimmed">{entry.email}</Text>
                        </Box>
                    </Group>
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Select data={STATUS_OPTIONS} value={String(entry.candidateStatus)} onChange={handleStatusChange} size="xs" w={130} disabled={upsertMutation.isPending} comboboxProps={{ withinPortal: true }} />
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{addedByName}</Text></Table.Td>
                <Table.Td>
                    <Text size="sm">{new Date(entry.addedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</Text>
                </Table.Td>
                <Table.Td>
                    {entry.fitScoreAtShortlist != null
                        ? <Badge size="sm" variant="light" color="sopraBlue">{entry.fitScoreAtShortlist}%</Badge>
                        : <Text size="sm" c="dimmed">—</Text>}
                </Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap={4} wrap="nowrap">
                        <Button size="xs" variant="subtle" color="gray" leftSection={<PencilIcon style={{ width: 12, height: 12 }} />} onClick={() => setEditingNotes(true)} disabled={editingNotes}>Edit</Button>
                        <Button size="xs" variant="light" color="red" leftSection={<TrashIcon style={{ width: 12, height: 12 }} />} loading={removeMutation.isPending} onClick={handleRemove}>Remove</Button>
                    </Group>
                </Table.Td>
            </Table.Tr>
            {(entry.notes || editingNotes) && (
                <Table.Tr style={{ backgroundColor: 'transparent' }}>
                    <Table.Td colSpan={6} pt={0} pb={8} pl={52} style={{ borderTop: 'none' }}>
                        {editingNotes ? (
                            <Stack gap={6} onClick={(e) => e.stopPropagation()}>
                                <Textarea value={notesValue} onChange={(e) => setNotesValue(e.currentTarget.value)} size="xs" autosize minRows={2} maxRows={8} placeholder="Add notes about this candidate…" style={{ maxWidth: 600 }} autoFocus />
                                <Group gap={6}>
                                    <Button size="xs" variant="filled" color="brand.5" leftSection={<CheckIcon style={{ width: 12, height: 12 }} />} onClick={handleSaveNotes} loading={upsertMutation.isPending}>Save</Button>
                                    <ActionIcon size="sm" variant="subtle" onClick={handleCancelEdit} disabled={upsertMutation.isPending} aria-label="Cancel"><XMarkIcon style={{ width: 12, height: 12 }} /></ActionIcon>
                                </Group>
                            </Stack>
                        ) : (
                            <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap', maxWidth: 700 }}>{entry.notes}</Text>
                        )}
                    </Table.Td>
                </Table.Tr>
            )}
        </>
    );
};

// ── Main tab ──────────────────────────────────────────────────────────────────

interface Props {
    opportunityId: number;
    positionId: number;
    onOpenPreview: (personId: number) => void;
}

export const PositionShortlistTab = ({ opportunityId, positionId, onOpenPreview }: Props) => {
    const { data: shortlistData, isLoading } = useOpportunityShortlistQuery(opportunityId, positionId);
    const [downloading, setDownloading] = useState(false);

    const handleDownloadCvs = async () => {
        if (!shortlistData || shortlistData.length === 0) { return; }
        setDownloading(true);
        try {
            const personIds = shortlistData.map((e) => e.personId);
            const blob = await new ExportService().bulkExportByIds(personIds);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Shortlist_CVs_${opportunityId}_${positionId}.zip`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setDownloading(false);
        }
    };

    if (isLoading) {
        return (
            <Group justify="center" py="md">
                <Loader size="sm" />
            </Group>
        );
    }

    if (!shortlistData || shortlistData.length === 0) {
        return (
            <Text size="sm" c="dimmed" ta="center" py="md">
                No candidates shortlisted yet. Use the Search tab to add people.
            </Text>
        );
    }

    return (
        <>
            {/* ── Desktop: table ── */}
            <Box visibleFrom="sm">
                <Table highlightOnHover withTableBorder={false} mt="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Name</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Added by</Table.Th>
                            <Table.Th>Added at</Table.Th>
                            <Table.Th>Fit score</Table.Th>
                            <Table.Th>
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="brand.5"
                                    leftSection={<ArrowDownTrayIcon style={{ width: 12, height: 12 }} />}
                                    loading={downloading}
                                    onClick={handleDownloadCvs}
                                >
                                    Download CVs
                                </Button>
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {shortlistData.map((entry) => (
                            <ShortlistRow key={entry.personId} entry={entry} opportunityId={opportunityId} onOpenPreview={onOpenPreview} />
                        ))}
                    </Table.Tbody>
                </Table>
            </Box>

            {/* ── Mobile: cards ── */}
            <Box hiddenFrom="sm">
                <Stack gap={0} mt="xs">
                    <Group justify="flex-end" pb="xs">
                        <Button
                            size="xs"
                            variant="light"
                            color="brand.5"
                            leftSection={<ArrowDownTrayIcon style={{ width: 12, height: 12 }} />}
                            loading={downloading}
                            onClick={handleDownloadCvs}
                        >
                            Download CVs
                        </Button>
                    </Group>
                    <Divider />
                    {shortlistData.map((entry) => (
                        <ShortlistCard key={entry.personId} entry={entry} opportunityId={opportunityId} onOpenPreview={onOpenPreview} />
                    ))}
                </Stack>
            </Box>
        </>
    );
};
